/**
 * CellFieldOverlay — the floating "field" that pops out of a DataTable cell.
 *
 * Attio-style inline editing renders the cell's selected/editing states as an
 * overlay anchored over the cell rect rather than as markup inside the cell:
 *
 * - `selected` (single click / roving focus): the cell appears lifted out of
 *   the grid — rounded corners, 2px focus border, elevated background, soft
 *   shadow — slightly overflowing the cell bounds, revealing the full
 *   (untruncated) value.
 * - `editing` (double click / Enter / F2): the *same* container hosts the
 *   editor and may grow beyond the column width to fit the draft value.
 *
 * A portal is load-bearing here, not a styling choice: gridcells are
 * `overflow-hidden`, and virtualized rows are `transform`-positioned (each row
 * is its own stacking context), so any in-flow element that tries to overhang
 * the cell is clipped by the cell or painted under the neighbouring row.
 *
 * Geometry: the overlay covers the anchor rect expanded by OVERLAY_INSET on
 * every side. Horizontal padding is chosen so the overlay's text starts at
 * exactly the same x as the cell's own text (cell px-3 = 12px), preventing any
 * visual jump between the in-grid value, the selected value, and the editor.
 *
 * The overlay re-anchors on scroll/resize (capture phase, rAF-throttled) so it
 * tracks the cell while the table or page scrolls. When the anchor unmounts
 * (virtualized row leaving the viewport) the owner unmounts the overlay.
 */

import type { ReactNode } from 'react';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { StackingLayer, useStackingZIndex } from '@/contexts/StackingContext';
import { cx } from '@/utils/cx';

/** How far the popped field overhangs the cell rect on each side (px). */
const OVERLAY_INSET = 3;

/**
 * Horizontal content padding (px). Chosen so text aligns with the cell's own
 * text: cell padding (12px, px-3) + OVERLAY_INSET (3) − border (2) = 13.
 */
const CONTENT_PAD_X = 13;

/** Cap how far the popped field can grow beyond the column. */
const MAX_WIDTH_PX = 560;

/**
 * Card-layout editors (multi-value lists) need room for their value rows —
 * drag handle + value + remove button — regardless of how narrow the column
 * is, so the field widens to at least this.
 */
const CARD_MIN_WIDTH_PX = 340;

/** Minimum gap kept between the field's right edge and the viewport (px). */
const VIEWPORT_GUTTER_PX = 8;

interface AnchorBox {
  top: number;
  left: number;
  width: number;
  height: number;
}

const readAnchorBox = (el: HTMLElement): AnchorBox => {
  const rect = el.getBoundingClientRect();
  return {
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
  };
};

export interface CellFieldOverlayProps {
  /** The gridcell element the field is anchored over. */
  anchorEl: HTMLElement;
  /** Visual/behavioural mode — same container, different content. */
  mode: 'selected' | 'editing';
  /**
   * Content layout inside the field. 'row' (default) is the single-line
   * treatment — content vertically centred, text aligned with the cell's
   * own text. 'card' stacks block content (multi-value editors: value rows
   * plus an add input) with card padding and a wider minimum. 'card-flush'
   * is 'card' without the padding — for editors that own their internal
   * sections (search header, option list, footer) and need their dividers
   * to span edge to edge.
   */
  layout?: 'row' | 'card' | 'card-flush';
  /**
   * Override for the card layouts' minimum width. The generic card floor
   * (CARD_MIN_WIDTH_PX) exists for editors that need real width to be usable
   * (association picker: search + option list). A compact editor whose
   * content defines its own natural width (the capped phone field) passes 0
   * so the card hugs its content / the cell instead of stretching to the
   * generic floor. Ignored for the 'row' layout.
   */
  cardMinWidthPx?: number;
  /** Ref to the overlay root, used by the owner for focus containment. */
  overlayRef?: React.RefObject<HTMLDivElement | null>;
  /**
   * Pin the field's width to the anchor (plus the inset) instead of letting
   * it grow to its content's max-content width (capped at MAX_WIDTH_PX). For
   * a block editor whose text wraps — a rich-text field popped out of a
   * record-panel row — max-content is the longest paragraph unwrapped, which
   * would widen the field past the panel edge; anchor-width keeps its lines
   * wrapping exactly where the read value's did.
   */
  fitAnchor?: boolean;
  /**
   * Mark the field as part of its host surface for interact-outside
   * dismissal. The portal puts the field outside a modal slideout's DOM, so
   * a click inside it reads as "outside" to the slideout — which would close
   * it mid-edit. `data-keep-overlay-open` is the slideout's own escape hatch
   * for exactly this (see slideout-menu.tsx shouldKeepOverlayOpen).
   */
  keepParentOverlayOpen?: boolean;
  /**
   * Whether a field taller than the space below its anchor shifts UP to stay
   * on-screen (the default — right for a grid cell, where the field is a
   * short row). Pass false for a click-to-edit host whose contract is "the
   * text stays where it is": the field then keeps its top on the anchor and
   * may extend past the viewport bottom; it re-anchors as the host scrolls,
   * so the user scrolls the rest into view. Horizontal clamping is kept.
   */
  clampToViewport?: boolean;
  /** Double click on the selected field enters edit mode. */
  onDoubleClick?: () => void;
  /**
   * Content rendered below the field, outside its overflow clip — e.g. the
   * validation error tooltip while editing.
   */
  footer?: ReactNode;
  children: ReactNode;
}

/**
 * Portal-rendered floating field anchored over a table cell.
 *
 * Mouse-down is prevented in `selected` mode so clicking the overlay never
 * blurs the gridcell underneath (blur would unmount the overlay mid-click and
 * eat the second click of a double click).
 */
export const CellFieldOverlay: React.FC<CellFieldOverlayProps> = ({
  anchorEl,
  mode,
  layout = 'row',
  cardMinWidthPx,
  overlayRef,
  fitAnchor = false,
  keepParentOverlayOpen = false,
  clampToViewport = true,
  onDoubleClick,
  footer,
  children,
}) => {
  const zIndex = useStackingZIndex();
  const [box, setBox] = useState<AnchorBox>(() => readAnchorBox(anchorEl));
  const rafRef = useRef<number | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  // Actual rendered size of the field — the wrapper is w-max, so content
  // (card minimums, growing editors, expanded multi-value lists) can be much
  // wider/taller than the anchor. Needed to keep the field on-screen for
  // cells near the viewport edges.
  const [measured, setMeasured] = useState<{
    width: number;
    height: number;
  } | null>(null);

  // Keep both refs populated: the owner's overlayRef (focus containment /
  // outside-click checks) and our own (measurement).
  const setRefs = useCallback(
    (el: HTMLDivElement | null) => {
      wrapperRef.current = el;
      if (overlayRef) {
        overlayRef.current = el;
      }
    },
    [overlayRef]
  );

  // Track the field's own size so the clamped position follows content
  // growth (typing into a width-growing editor, values loading into a card).
  useLayoutEffect(() => {
    const el = wrapperRef.current;
    if (!el) {
      return;
    }
    setMeasured({ width: el.offsetWidth, height: el.offsetHeight });
    const observer = new ResizeObserver(() => {
      setMeasured({ width: el.offsetWidth, height: el.offsetHeight });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Re-measure synchronously when the anchor changes (cell-to-cell moves).
  useLayoutEffect(() => {
    setBox(readAnchorBox(anchorEl));
  }, [anchorEl]);

  // Track the anchor while any ancestor scrolls or the window resizes.
  // Capture phase catches the table's internal overflow-auto pane, which
  // scroll events do not bubble out of.
  useEffect(() => {
    const reposition = () => {
      if (rafRef.current !== null) {
        return;
      }
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        setBox(readAnchorBox(anchorEl));
      });
    };
    window.addEventListener('scroll', reposition, { capture: true });
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, { capture: true });
      window.removeEventListener('resize', reposition);
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [anchorEl]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (mode === 'selected') {
        // Keep focus on the gridcell — the overlay is a visual proxy for it.
        e.preventDefault();
      }
      e.stopPropagation();
    },
    [mode]
  );

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const handleDoubleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onDoubleClick?.();
    },
    [onDoubleClick]
  );

  const minWidth = Math.max(
    box.width + OVERLAY_INSET * 2,
    layout === 'row' ? 0 : (cardMinWidthPx ?? CARD_MIN_WIDTH_PX)
  );
  const minHeight = box.height + OVERLAY_INSET * 2;

  // Anchor at the cell, then clamp into the viewport: a field wider/taller
  // than the space right/below of a near-edge cell shifts left/up (against
  // its measured size) rather than rendering off-screen. The width cap is
  // viewport-relative, not left-relative, since the shift reclaims space.
  const width = Math.max(measured?.width ?? 0, minWidth);
  const height = Math.max(measured?.height ?? 0, minHeight);
  const top = clampToViewport
    ? Math.max(
        VIEWPORT_GUTTER_PX,
        Math.min(
          box.top - OVERLAY_INSET,
          window.innerHeight - VIEWPORT_GUTTER_PX - height
        )
      )
    : box.top - OVERLAY_INSET;
  const left = Math.max(
    VIEWPORT_GUTTER_PX,
    Math.min(
      box.left - OVERLAY_INSET,
      window.innerWidth - VIEWPORT_GUTTER_PX - width
    )
  );

  return createPortal(
    <div
      ref={setRefs}
      data-cell-field-overlay={mode}
      data-keep-overlay-open={keepParentOverlayOpen ? '' : undefined}
      style={{
        position: 'fixed',
        top,
        left,
        maxWidth: Math.min(
          MAX_WIDTH_PX,
          window.innerWidth - VIEWPORT_GUTTER_PX * 2
        ),
        // fitAnchor: the field is exactly the anchor's width (+ inset) so
        // wrapping text keeps the read value's line breaks; otherwise w-max
        // lets width-growing editors expand toward MAX_WIDTH_PX.
        ...(fitAnchor && { width: minWidth }),
        zIndex,
      }}
      // relative so the footer can hang below without contributing to the
      // wrapper's width (a long error message must not stretch the field).
      className={cx('relative', !fitAnchor && 'w-max')}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
    >
      <div
        style={{
          minWidth,
          minHeight,
          ...(layout === 'row' && {
            paddingLeft: CONTENT_PAD_X,
            paddingRight: CONTENT_PAD_X,
          }),
          // Width-growing editors: the input starts at the cell's inner width
          // and grows with content (see EditableCell's field-sizing override).
          ['--cell-field-width' as string]: `${minWidth - CONTENT_PAD_X * 2 - 4}px`,
        }}
        className={cx(
          'max-w-full overflow-hidden rounded-lg border-2',
          'border-[var(--color-focus-ring)] bg-[var(--color-bg-primary)] shadow-md',
          layout === 'row' && 'flex items-center',
          // Card layout: block content (value rows + add input) with its own
          // padding; the deeper shadow matches its popover-card lineage.
          layout === 'card' && 'px-2.5 py-2 shadow-lg',
          layout === 'card-flush' && 'shadow-lg'
        )}
      >
        <StackingLayer>{children}</StackingLayer>
      </div>
      {footer && (
        <div className='absolute left-0 top-full w-max max-w-full'>
          {footer}
        </div>
      )}
    </div>,
    document.body
  );
};
